import React, { Fragment, useMemo, useState } from 'react';
import { Container, Row, Col, Card, CardBody, CardHeader, Button, Badge } from 'reactstrap';
import { Breadcrumbs } from '../../../../AbstractElements';
import DataTable from 'react-data-table-component';
import { useNavigate } from 'react-router-dom';
import { useGetPurchaseOrdersQuery } from '../../../../store/services/inventoryApi';
import { Edit, Eye, Plus } from 'react-feather';

const PurchaseOrders = () => {
    const navigate = useNavigate();
    const [statusFilter, setStatusFilter] = useState('');
    const { data: posData, isLoading } = useGetPurchaseOrdersQuery({ status: statusFilter });

    const purchaseOrders = useMemo(() => {
        if (!posData) return [];
        return Array.isArray(posData) ? posData : (posData.data || []);
    }, [posData]);

    const columns = [
        {
            name: 'PO Number',
            selector: row => row.poNumber,
            sortable: true,
            grow: 1.5
        },
        {
            name: 'Supplier',
            selector: row => row.supplierId?.name || 'N/A',
            sortable: true,
        },
        {
            name: 'Store',
            selector: row => row.storeId?.storeName || 'N/A',
            sortable: true,
        },
        {
            name: 'Date',
            selector: row => new Date(row.createdAt).toLocaleDateString(),
            sortable: true,
        },
        {
            name: 'Status',
            selector: row => row.status,
            sortable: true,
            cell: row => {
                let color = 'secondary';
                if (row.status === 'Approved') color = 'info';
                if (row.status === 'Ordered') color = 'primary';
                if (row.status === 'PartiallyReceived') color = 'warning';
                if (row.status === 'Closed') color = 'success';
                if (row.status === 'Received') color = 'warning';
                if (row.status === 'Completed') color = 'success';
                return <Badge color={color}>{row.status}</Badge>;
            }
        },
        {
            name: 'Total Cost',
            selector: row => row.totalAmount || 0,
            sortable: true,
            format: row => `$${(row.totalAmount || 0).toFixed(2)}`
        },
        {
            name: 'Actions',
            cell: row => (
                <div className="d-flex gap-2">
                    <Button color="info" size="xs" onClick={() => navigate(`/inventory/purchase-orders/${row._id}`)} title="View Details">
                        <Eye size={14} />
                    </Button>
                    {row.status === 'Draft' && (
                        <Button color="warning" size="xs" onClick={() => navigate(`/inventory/purchase-orders/edit/${row._id}`)} title="Edit PO">
                            <Edit size={14} />
                        </Button>
                    )}
                </div>
            )
        }
    ];

    return (
        <Fragment>
            <Breadcrumbs mainTitle="Purchase Orders" parent="Inventory" title="List" />
            <Container fluid={true}>
                <Row>
                    <Col sm="12">
                        <Card>
                            <CardHeader className="d-flex justify-content-between align-items-center">
                                <h5>Purchase Order List</h5>
                                <Button color="primary" onClick={() => navigate('/inventory/purchase-orders/create')}>
                                    <Plus size={14} className="me-1" /> Create PO
                                </Button>
                            </CardHeader>
                            <CardBody>
                                <div className="mb-3 d-flex gap-2">
                                    <Button color={statusFilter === '' ? 'primary' : 'light'} size="sm" onClick={() => setStatusFilter('')}>All</Button>
                                    <Button color={statusFilter === 'Draft' ? 'primary' : 'light'} size="sm" onClick={() => setStatusFilter('Draft')}>Draft</Button>
                                    <Button color={statusFilter === 'Approved' ? 'primary' : 'light'} size="sm" onClick={() => setStatusFilter('Approved')}>Approved</Button>
                                    <Button color={statusFilter === 'Ordered' ? 'primary' : 'light'} size="sm" onClick={() => setStatusFilter('Ordered')}>Ordered</Button>
                                    <Button color={statusFilter === 'PartiallyReceived' ? 'primary' : 'light'} size="sm" onClick={() => setStatusFilter('PartiallyReceived')}>Partially Received</Button>
                                    <Button color={statusFilter === 'Closed' ? 'primary' : 'light'} size="sm" onClick={() => setStatusFilter('Closed')}>Closed</Button>
                                </div>
                                <DataTable
                                    columns={columns}
                                    data={purchaseOrders}
                                    pagination
                                    progressPending={isLoading}
                                    highlightOnHover
                                    striped
                                    responsive
                                    noDataComponent={<div className="p-4 text-center text-muted">No purchase orders found</div>}
                                />
                            </CardBody>
                        </Card>
                    </Col>
                </Row>
            </Container>
        </Fragment>
    );
};

export default PurchaseOrders;
